import React, { useContext, useEffect, useState } from "react";
import { VisibleStore, IDispatchClient } from "./model";
import { CurrentTimeContext } from "./current_time_context";
import { formatDay, formatShortDate, formatTime } from "./formatting";

const CLOSING_SOON_MS = 30 * 60 * 1000;

type DisplayedStoreStatus = "open" | "closingSoon" | "lastOrdersPassed" | "paused" | "pausedPastLastOrders" | "closedForToday" | "disabled";

const displayedStoreStatus = (store: VisibleStore, now: Date): DisplayedStoreStatus => {
  if (store.disabled) return "disabled";
  if (store.closesAt == null || store.lastOrdersAt == null || store.closesAt <= now) return "closedForToday";
  if (store.pausedUntil && store.pausedUntil > now) {
    // A pause outlasting the cutoff means nothing more is coming today, whatever its end time says
    return store.pausedUntil >= store.lastOrdersAt ? "pausedPastLastOrders" : "paused";
  }
  if (store.lastOrdersAt <= now) return "lastOrdersPassed";
  if (store.lastOrdersAt.getTime() - now.getTime() <= CLOSING_SOON_MS) return "closingSoon";
  return "open";
};

const takesNoOrders = (status: DisplayedStoreStatus): boolean =>
  status === "closedForToday" || status === "disabled" || status === "pausedPastLastOrders";

interface DisplayedStore {
  store: VisibleStore;
  status: DisplayedStoreStatus;
}

const sortedForDisplay = (stores: VisibleStore[], now: Date): DisplayedStore[] =>
  stores
    .map((store) => ({ store, status: displayedStoreStatus(store, now) }))
    .sort((a, b) => {
      const aClosed = takesNoOrders(a.status) ? 1 : 0;
      const bClosed = takesNoOrders(b.status) ? 1 : 0;
      if (aClosed !== bClosed) return aClosed - bClosed;

      const aCloses = a.store.closesAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bCloses = b.store.closesAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
      if (aCloses !== bCloses) return aCloses - bCloses;

      return a.store.name.localeCompare(b.store.name);
    });

const formatPausedUntil = (pausedUntil: Date, now: Date): string => {
  if (formatDay(pausedUntil) === formatDay(now)) {
    return formatTime(pausedUntil);
  }
  return `${formatShortDate(pausedUntil)}, ${formatTime(pausedUntil)}`;
}

const minutesUntil = (date: Date, now: Date): number =>
  Math.ceil((date.getTime() - now.getTime()) / 60000);

const StatusTag = ({ store, status, now }: { store: VisibleStore; status: DisplayedStoreStatus; now: Date }): React.JSX.Element => {
  switch (status) {
    case "open":
      return <span className="tag is-success is-light">Open</span>;
    case "closingSoon":
      return <span className="tag is-warning is-light">Closing soon</span>;
    case "lastOrdersPassed":
      return <span className="tag is-warning is-light">Last orders passed</span>;
    case "paused":
      return <span className="tag is-warning is-light">Paused until {formatPausedUntil(store.pausedUntil as Date, now)}</span>;
    case "pausedPastLastOrders":
      // A pause ending on a later day is still worth dating; one ending today would only mislead
      return formatDay(store.pausedUntil as Date) === formatDay(now)
        ? <span className="tag is-light">Paused past last orders</span>
        : <span className="tag is-light">Paused until {formatPausedUntil(store.pausedUntil as Date, now)}</span>;
    case "closedForToday":
      return <span className="tag is-light">Closed for today</span>;
    case "disabled":
      return <span className="tag is-light">Disabled</span>;
  }
};

const StoreRow = ({ store, status, now }: { store: VisibleStore; status: DisplayedStoreStatus; now: Date }): React.JSX.Element => {
  const closed = takesNoOrders(status);

  return (
    <tr className={status === "closingSoon" ? "is-closing-soon" : closed ? "is-closed" : ""}>
      <td>
        <a href={store.href} target="_blank" rel="noreferrer">{store.name}</a>
        {store.windowsToday.length > 1 ? (
          <span className="store-windows">
            {store.windowsToday.map((window, index) => (
              <React.Fragment key={index}>
                {index > 0 ? " · " : null}
                <span className="is-nowrap">{formatTime(window.start)}–{formatTime(window.end)}</span>
              </React.Fragment>
            ))}
          </span>
        ) : null}
      </td>
      <td className="is-narrow"><StatusTag store={store} status={status} now={now} /></td>
      <td className="has-text-right is-narrow">
        {store.lastOrdersAt && !closed ? (
          <>
            {formatTime(store.lastOrdersAt)}
            {status === "closingSoon" ? <span className="closing-soon-note"> · in {minutesUntil(store.lastOrdersAt, now)}m</span> : null}
          </>
        ) : "—"}
      </td>
      <td className="has-text-right is-narrow">
        {store.closesAt == null ? "no hours today" : status === "closedForToday" ? `closed ${formatTime(store.closesAt)}` : formatTime(store.closesAt)}
      </td>
    </tr>
  );
};

type LoadState =
  | { state: "loading" }
  | { state: "error" }
  | { state: "loaded"; stores: VisibleStore[] };

const StoresOverlay = ({ apiClient, onClose }: { apiClient: IDispatchClient; onClose: () => void }): React.JSX.Element => {
  const now = useContext(CurrentTimeContext);
  const [loadState, setLoadState] = useState<LoadState>({ state: "loading" });

  useEffect(() => {
    let cancelled = false;

    apiClient.getStores()
      .then((stores) => { if (!cancelled) setLoadState({ state: "loaded", stores }); })
      .catch(() => { if (!cancelled) setLoadState({ state: "error" }); });

    return () => { cancelled = true; };
  }, [apiClient]);

  return (
    <div className="modal is-active dispatch-stores-overlay" data-testid="stores-overlay">
      <div className="modal-background" onClick={onClose}></div>
      <div className="modal-card">
        <header className="modal-card-head">
          <span className="dispatch-stores-overlay-title">Stores</span>
          <button className="delete" aria-label="Close" onClick={onClose}></button>
        </header>

        <section className="modal-card-body">
          {loadState.state === "loading" ? <p className="has-text-grey">Loading…</p> : null}
          {loadState.state === "error" ? <p>{"Couldn't load the visible stores. Feel free to retry."}</p> : null}
          {loadState.state === "loaded" ? (
            <table className="table is-fullwidth is-narrow has-text-monospaced-numbers">
              <thead>
                <tr>
                  <th>Store</th>
                  <th className="is-narrow">Status</th>
                  <th className="has-text-right is-narrow">Last orders</th>
                  <th className="has-text-right is-narrow">Closes</th>
                </tr>
              </thead>
              <tbody>
                {sortedForDisplay(loadState.stores, now).map(({ store, status }) => (
                  <StoreRow key={store.storeId} store={store} status={status} now={now} />
                ))}
              </tbody>
            </table>
          ) : null}
        </section>

        <footer className="modal-card-foot">
          <span className="key-hint"><kbd>Esc</kbd> to close</span>
        </footer>
      </div>
    </div>
  );
};

export { StoresOverlay, sortedForDisplay, displayedStoreStatus };
